///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2022
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useState } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import { format } from "date-fns";

import { division, dateFormat } from "../../../helpers/types";
import PanelStateIndicator from "../PanelStateIndicator";
import EditIcon from "../../icons/EditIcon";
import PercentProgressBar from "../../PercentProgressBar";
import Avatar from "../../Avatar";
import LaneAssignmentModal from "../LaneAssignmentModal";
import messages from "../../../helpers/messages";
import { roundForNullableField } from "../../../helpers/laneCalculations";

const ProfilePicture = ({ owner }) => {
  return (
    <Avatar
      className="inline-block mb-0 align-middle"
      imageSource={owner.profilePicture}
      size={36}
      alt={owner.fullName}
      textContent={`${owner.firstName[0]}${owner.lastName[0]}`}
      fontSize={12}
    />
  );
};

export const Assignments = ({ fetchDetails, bidId }) => {
  const [assignmentModal, toggleAssignmentModal] = useState(false);
  const { tlLaneCountByRegion, revenue } = useSelector(
    state => state.bidSummary
  );
  const { user } = useSelector(state => state.user);
  const error = tlLaneCountByRegion.isFailure;
  const loading = tlLaneCountByRegion.isFetching;
  let totalLaneCount = 0;
  let editedLaneCount = 0;
  tlLaneCountByRegion.lanesByRegion.forEach(laneRegion => {
    totalLaneCount += laneRegion.laneCount;
    editedLaneCount += laneRegion.editedCount;
  });
  const regionsWithAssignedUsers = tlLaneCountByRegion.lanesByRegion.filter(
    laneRegion => !!laneRegion.assignedUser
  );
  const systemDateFormat =
    user.division === division.coy ? dateFormat.MMddyy : dateFormat.ddMMyy;
  const percentEdited = totalLaneCount
    ? Math.round((editedLaneCount / totalLaneCount) * 100)
    : 0;

  return (
    <div className="mt-16 card pos-relative">
      {assignmentModal && (
        <LaneAssignmentModal
          bidId={bidId}
          handleClose={() => toggleAssignmentModal(false)}
        />
      )}
      <div
        className="card__heading--large pos-relative uppercase widget justify-between"
        data-testid="accordion-title"
      >
        {messages.assignments.defaultMessage}
        {tlLaneCountByRegion.lanesByRegion.length > 0 && (
          <button
            data-testid="edit-assignments"
            className="no-focus"
            onClick={() => toggleAssignmentModal(true)}
          >
            <EditIcon
              height="18px"
              width="18px"
              className="cursor-pointer fr m-16 mr-24"
            />
          </button>
        )}
      </div>
      <div
        style={{ height: 468 }}
        className={`border-top-grey scroll scroll-on-hover py-24 pl-24 pr-16 mr-8 box ${
          !loading && !error && "items-start"
        }`}
      >
        <PanelStateIndicator
          loading={loading}
          error={error}
          handleRefresh={fetchDetails}
        />
        {error || loading || (
          <div className="one-whole">
            <div className="mb-12">
              <span className="mr-4 font-size-24">{percentEdited}%</span>
              <span className="font-size-14">
                {messages.lanesEdited.defaultMessage}
              </span>
              {revenue !== null && (
                <span className="fr font-size-14 pt-12" data-testid="revenue">
                  {messages.projectedRevenue.defaultMessage}: $
                  {roundForNullableField(revenue)}
                </span>
              )}
            </div>
            <PercentProgressBar percent={percentEdited} />
            <div className="block text-center mt-12">
              <span className="dot dot--green"></span>
              <span className="m-12 font-size-14">
                <FormattedMessage
                  {...messages.editedLanesCount}
                  values={{ editedLanesCount: editedLaneCount }}
                />
              </span>
              <span className="dot ml-40"></span>
              <span className="m-12 font-size-14">
                <FormattedMessage
                  {...messages.totalLanesCount}
                  values={{ totalLanesCount: totalLaneCount }}
                />
              </span>
            </div>
            <table className="mt-20 assignments-table">
              <thead>
                <tr className="header-row with-top-border h-40">
                  <th className="header-cell left">
                    {messages.team.defaultMessage}
                  </th>
                  <th className="left">{messages.assigned.defaultMessage}</th>
                  <th className="left">{messages.edited.defaultMessage}</th>
                  <th className="left">
                    {messages.dateAssigned.defaultMessage}
                  </th>
                  <th className="left">{messages.lastEdited.defaultMessage}</th>
                </tr>
              </thead>
              <tbody>
                {regionsWithAssignedUsers.length > 0 &&
                  regionsWithAssignedUsers.map(laneRegion => (
                    <tr
                      className="row h-48"
                      key={`${laneRegion.assignedUser.userId}-assignment-${laneRegion.regionId}`}
                    >
                      <td>
                        <ProfilePicture owner={laneRegion.assignedUser} />
                      </td>
                      <td>{laneRegion.laneCount}</td>
                      <td>{laneRegion.editedCount}</td>
                      <td>
                        {format(
                          new Date(laneRegion.assignedDateUTC),
                          systemDateFormat
                        )}
                      </td>
                      <td>
                        {laneRegion.lastEditedDateUTC
                          ? format(
                              new Date(laneRegion.lastEditedDateUTC),
                              systemDateFormat
                            )
                          : ""}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default Assignments;
